import type { components } from '@pba/types-openapi-client';
import { type UserDetailConsent, createUserDetailConsent } from './consents';
import type { EducationLevel } from './education-level';
import type { Gender } from './gender';
import type { Interest } from './interest';
import { type UserList, createUserList } from './list';
import { type Membership, type MembershipsCount, createMembership, createMembershipsCount } from './memberships';
import { type Permissions, createPermissions } from './permissions';
import {
  type BadgeNotificationsSettings,
  type EmailNotificationsSettings,
  type PushNotificationsSettings,
  type UserSettings,
  createBadgeNotificationsSettings,
  createEmailNotificationsSettings,
  createPushNotificationsSettings,
  createUserSettings,
} from './settings';

// TODO add missing properties when needed
export type UserDetail = {
  apt?: string;
  authorPrograms: string[];
  badgeNotificationsSettings: BadgeNotificationsSettings;
  biography?: string;
  birthdate?: string;
  city?: string;
  company?: string;
  country?: string;
  educationLevel?: EducationLevel;
  emailNotificationsSettings: EmailNotificationsSettings;
  gender?: Gender;
  interests?: Interest[];
  maintenance: boolean;
  memberships: Membership[];
  membershipsCount: MembershipsCount;
  nodeId?: string;
  permissions: Permissions;
  phone?: string;
  primaryOrganizationId?: string;
  pushNotificationsSettings?: PushNotificationsSettings;
  requiredConsents: UserDetailConsent[];
  settings: UserSettings;
  state?: string;
  street?: string;
  superadmin: boolean;
  timeZone?: string;
  timeZoneChanged: boolean | null;
  usersConsents: UserDetailConsent[];
  zip?: string;
} & UserList;

export const createUserDetail = (data: components['schemas']['UserDetail']): UserDetail => ({
  ...createUserList(data),
  apt: data.apt,
  authorPrograms: data.author_programs,
  badgeNotificationsSettings: createBadgeNotificationsSettings(data.badge_notifications_settings),
  biography: data.biography,
  birthdate: data.birthdate,
  city: data.city,
  company: data.company,
  country: data.country,
  educationLevel: data.education_level,
  emailNotificationsSettings: createEmailNotificationsSettings(data.email_notifications_settings),
  gender: data.gender,
  interests: data.interests,
  maintenance: data.maintenance,
  memberships: data.memberships.map((membership) => createMembership(membership)),
  membershipsCount: createMembershipsCount(data.memberships_count),
  nodeId: data.node_id,
  permissions: createPermissions(data.permissions),
  phone: data.phone,
  primaryOrganizationId: data.primary_organization_id,
  pushNotificationsSettings: data.push_notifications_settings
    ? createPushNotificationsSettings(data.push_notifications_settings)
    : undefined,
  requiredConsents: data.required_consents.map((consent) => createUserDetailConsent(consent)),
  settings: createUserSettings(data.settings),
  state: data.state,
  street: data.street,
  superadmin: data.superadmin,
  timeZone: data.time_zone,
  timeZoneChanged: data.time_zone_changed,
  usersConsents: data.users_consents.map((consent) => createUserDetailConsent(consent)),
  zip: data.zip,
});
